import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { createTransform, persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { thunk } from "redux-thunk";

import adminDashboardReducer from "./admin/adminDashboardSlice";
import bookmarkReducer from "./bookmark/slice";
import coachingReducer from "./coaching/slice";
import completionReducer from "./completion/slice";
import credentialClearanceReducer from "./credentialClearance/slice";
import dashboardReducer from "./dashboard/slice";
import economyReducer from "./economy/slice";
import experienceProjectionReducer from "./experienceProjection/slice";
import journeyReducer from "./journey/slice";
import keyshawnReducer from "./keyshawn/slice";
import leaderboardReducer from "./leaderboard/slice";
import notificationReducer from "./notification";
import performanceReducer from "./performance";
import planBoardReducer from "./planBoard/slice";
import planTasksReducer from "./planTasks/slice";
import profileReducer from "./profile/slice";
import seasonReducer from "./season/slice";
import shipWalkthroughReducer from "./shipWalkthrough/slice";
import squadActivityReducer from "./squadActivity/slice";
import taskReducer from "./tasks/slice";
import userReducer, { initialExperienceContext } from "./user/slice";
import workspaceReducer from "./workspace/slice";

const combinedReducer = combineReducers({
  user: userReducer,
  profile: profileReducer,
  leaderboard: leaderboardReducer,
  bookmark: bookmarkReducer,
  coaching: coachingReducer,
  dashboard: dashboardReducer,
  notification: notificationReducer,
  performance: performanceReducer,
  planTasks: planTasksReducer,
  // v6 plan board (master plan 6). Session-only and deliberately NOT in the
  // persist whitelist below: it is server state for one squad's board, the
  // server decides whether the board is v6 at all, and a persisted copy could
  // show one squad's board to the next account on a shared machine before the
  // first snapshot lands.
  planBoard: planBoardReducer,
  season: seasonReducer,
  tasks: taskReducer,
  journey: journeyReducer,
  // Credential Act-1 clearance/journey read state (F1b). Session-only -
  // deliberately NOT in the persist whitelist, so a re-open always re-reads
  // authoritative server state (the read endpoint is the source of truth).
  credentialClearance: credentialClearanceReducer,
  // W3-F1 the authenticated-self completion read. Session-only and NOT in the
  // persist whitelist below, for the same reason the projection is not: it
  // carries per-caller facts (the caller's check-ins, their doors, their own
  // sentence), and a persisted copy could show one learner's record to the next
  // account on a shared machine before the first read lands. The server flag it
  // carries is the enablement gate, so a persisted `enabled: true` would also
  // render a sequence the server had since turned off.
  completion: completionReducer,
  // W4-F1 the authenticated-self economy read. Session-only and NOT in the
  // persist whitelist below, for the same three reasons `completion` is not: it
  // carries per-caller facts (the caller's own XP and streak, their squad's
  // standing, their output's floor state), a persisted copy could show one
  // learner's standing to the next account on a shared machine before the first
  // read lands, and the server flag it carries is the enablement gate, so a
  // persisted `enabled: true` would render a reward beat and a Vault slot for a
  // cohort the server had since turned off.
  economy: economyReducer,
  // Wave 2 shared experience projection (Chip P2). Session-only, deliberately
  // NOT in the persist whitelist below: the endpoint is the only authority for
  // what this actor may read, so a persisted snapshot could show one squad's
  // submission facts to a different learner before the first fetch lands. The
  // LOGOUT reset in rootReducer below covers it for free.
  experienceProjection: experienceProjectionReducer,
  // Keyshawn summonable drawer (F8). Intentionally NOT in
  // redux-persist whitelist below - drawer state is session-only.
  keyshawn: keyshawnReducer,
  // W4-F2.10 the ship-walkthrough mirror (Addendum 5 rule 4). Session-only and
  // deliberately NOT in the persist whitelist below: the DURABLE record is the
  // ending ledger in `Completion/endingState.js`, and a second persisted copy
  // could rehydrate disagreeing with it. This slice is hydrated from that ledger
  // whenever the (learner, challenge) scope is established or changes, so a
  // reload rebuilds it from the one authority rather than from storage twice.
  shipWalkthrough: shipWalkthroughReducer,
  // FE1 squad workspace server cache. Session-only and deliberately NOT in the
  // persist whitelist below, for the reasons `economy` and `completion` are
  // not: it carries squad files, task links and authorization state for one
  // context, the server is the only authority on whether this cohort has a
  // workspace at all, and a persisted copy could show one learner's squad files
  // to the next account on a shared machine before the first read lands.
  workspace: workspaceReducer,
  // Dashboard social, Slice 2: the squad activity read. Session-only and
  // deliberately NOT persisted (the persist config below is unchanged): it
  // carries one squad's work titles and one learner's read state (which
  // teammate events they have seen), and a persisted copy could show them to
  // the next account on a shared machine before the first read lands. LOGOUT
  // resets it with the rest.
  squadActivity: squadActivityReducer,
  adminDashboard: adminDashboardReducer,
});

const rootReducer = (state, action) => {
  if (action.type === "LOGOUT") {
    storage.removeItem("persist:root");
    return combinedReducer(undefined, action);
  }
  return combinedReducer(state, action);
};

// Two fields on the user slice are session-only and must never survive a
// reload: `missionAdvancePin` (a client-side repair for a mission response
// that was in flight during a level advance) and `latestMissionRequestId` (an
// ordering token for the CURRENT session's in-flight fetches). A persisted pin
// could hold a student on a level the server never advanced them to; a
// persisted request id could make the first fetch of a new session look stale
// and be discarded.
//
// This is enforced at the PERSISTENCE BOUNDARY rather than with a REHYDRATE
// case in the slice, because autoMergeLevel1 replaces an unmodified substate
// with the inbound payload AFTER the slice reducer has run, so a slice-level
// reset is simply overwritten. A transform runs on the value itself, in both
// directions, so the fields never reach storage and never come back from it.
//
// They are nulled rather than deleted so a rehydrated user substate stays
// shape-identical to initialState (autoMergeLevel1 swaps the whole substate in,
// so a deleted key would read `undefined` instead of `null`).
//
// M2 adds the delivery context (decision 4): the selected context is NEVER
// persisted. It is rediscovered on every load, which makes it invisible for a
// single-context learner and stops a stale context surviving an account
// switch in the same browser. It is reset to a fresh idle discovery state
// rather than nulled, for the same shape-identity reason. The newest mission
// request's generation and the active context's mission error go with it.
const SESSION_ONLY_USER_FIELDS = [
  "missionAdvancePin",
  "latestMissionRequestId",
  "latestMissionGeneration",
  "missionError",
  // Experience switcher (B1): the active context's own challenge read.
  "activeChallenge",
];

const stripSessionOnlyUserFields = (userState) => {
  if (!userState || typeof userState !== "object") return userState;
  const next = { ...userState };
  SESSION_ONLY_USER_FIELDS.forEach((field) => {
    next[field] = null;
  });
  next.experienceContext = initialExperienceContext();
  return next;
};

export const sessionOnlyUserTransform = createTransform(
  // inbound: on its way to storage.
  (inboundState) => stripSessionOnlyUserFields(inboundState),
  // outbound: on its way back out of storage at rehydrate.
  (outboundState) => stripSessionOnlyUserFields(outboundState),
  { whitelist: ["user"] },
);

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["user", "dashboard"],
  transforms: [sessionOnlyUserTransform],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST"],
      },
    }).concat(thunk),
});

export const persistor = persistStore(store);

export default store;
